import React from "react";
import { Controller, FieldErrors, Control } from "react-hook-form";

interface DependentAgesFieldsProps {
  numberOfDependents: number;
  control: Control<any>;
  errors: FieldErrors<any>;
  fieldName?: string;
  disabled?: boolean;
}

const DependentAgesFields: React.FC<DependentAgesFieldsProps> = ({
  numberOfDependents,
  control,
  errors,
  fieldName = "",
  disabled = false,
}) => {
  const getNestedError = (path: string) => {
    return path.split(".").reduce((errObj, key) => {
      if (errObj && errObj[key]) {
        return errObj[key];
      }
      return undefined;
    }, errors as any);
  };

  const hasErrors = () => {
    const fieldErrors = getNestedError(fieldName);
    if (!fieldErrors) return false;

    if (Array.isArray(fieldErrors)) {
      return fieldErrors.some((error) => !!error);
    }

    return Object.values(fieldErrors).some((error) => !!error);
  };

  const getErrorMessage = () => {
    const fieldErrors = getNestedError(fieldName);
    if (!fieldErrors) return null;

    // Check if any age is over 18
    const hasOver18 = Object.values(fieldErrors).some(
      (error: any) => error?.type === "max"
    );

    // Check if any required field is missing
    const hasMissing = Object.values(fieldErrors).some(
      (error: any) => error?.type === "required"
    );
    const hasZero = Object.values(fieldErrors).some(
      (error: any) => error?.type === "min"
    );

    // Check if any invalid number
    const hasInvalid = Object.values(fieldErrors).some(
      (error: any) => error?.type === "validate"
    );

    if (hasZero) {
      return "Dependent ages must be greater than 0";
    }
    if (hasOver18) {
      return "All dependents must be under 18 years old";
    }
    if (hasMissing) {
      return "Please fill all required dependent ages";
    }
    if (hasInvalid) {
      return "Please enter valid numbers for all ages";
    }

    return "Please correct the errors in dependent ages";
  };

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {numberOfDependents > 0 &&
        Array.from({ length: numberOfDependents }, (_, index) => {
          const fieldPath = `${fieldName}.${index}`;
          const fieldError = getNestedError(fieldPath);
          return (
            <>
              <div key={index} className="w-full sm:w-[25%]">
                <label className="font-Mulish text-[16px] sm:text-base font-normal text-lightBlue mb-1 flex items-center gap-1">
                  {`Age ${index + 1}`} <span className="text-red-500">*</span>
                </label>
                <Controller
                  name={fieldPath}
                  control={control}
                  // rules={{
                  //   required: `Age ${index + 1} is required`,
                  //   min: {
                  //     value: 1,
                  //     message: `Age ${index + 1} must be at least 1`,
                  //   },
                  //   max: {
                  //     value: 18,
                  //     message: `Dependent must be under 18`,
                  //   },
                  //   validate: (value) =>
                  //     !isNaN(Number(value)) || "Must be a number",
                  // }}
                  rules={
                    disabled
                      ? {}
                      : {
                          required: `Age ${index + 1} is required`,
                          min: {
                            value: 1,
                            message: `Age ${index + 1} must be at least 1`,
                          },
                          max: {
                            value: 18,
                            message: `Dependent must be under 18`,
                          },
                          validate: (value) =>
                            !isNaN(Number(value)) || "Must be a number",
                        }
                  }
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      placeholder="00"
                      disabled={disabled}
                      className={`w-full px-4 py-4 border-2 font-Mulish rounded-[10px] focus:outline-none placeholder-gray-400 ${
                        fieldError ? "border-red-500" : "border-[#EAECF0]"
                      }`}
                    />
                  )}
                />
              </div>
            </>
          );
        })}

      {hasErrors() && (
        <p className="text-red-500 text-sm mt-1 w-full text-left">
          {getErrorMessage()}
        </p>
      )}
    </div>
  );
};

export default DependentAgesFields;
